import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import {
  FETCH_ALL_LAST_DATA_DUMP_ROWS_INTERVAL,
  FETCH_SINGLE_LAST_DATA_DUMP_ROWS_INTERVAL,
} from "constants/intervals";
import {
  UPDATE_LAST_DATA_DUMP_ROW,
  UPDATE_LAST_DATA_DUMP_ROW_PER_PROCESS,
} from "providers/GlobalStateProvider";
import {
  DATA_DUMP_ROWS_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import { startTransition, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useInterval } from "react-use";
import { cleanRawDataDumpRow } from "segments/DataDumpGetter/dataDumpGetterUtils";
import { useSelector } from "store/useSelector";
import { DataDumpRow } from "types/types";
import {
  useIsSingleProcessPage,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ONE_MINUTE_OF_MS } from "utils/timeUtils";
import useQueryWithErrorHandling from "utils/useQuery";
import { useFlightConfig } from "./FlightConfig/useFlightConfig";

/** only used on multi-process pages -- fetch the lastDataDumpRow for the processes on this page  */
export function LastDataDumpRowPerProcessGetter() {
  const visibleProcessIds = useVisibleProcessIds();
  const isSingleProcessPage = useIsSingleProcessPage();
  const ready = !isSingleProcessPage && visibleProcessIds.length > 0;
  const [toggle, setToggle] = useState(true);
  const [, setLastDataDumpRowPerProcess] = useLastDataDumpRowPerProcess();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  // refetch on interval
  useInterval(() => {
    setToggle(false);
    setTimeout(() => setToggle(true));
  }, FETCH_ALL_LAST_DATA_DUMP_ROWS_INTERVAL * multiplyNetworkRequestIntervals);
  return ready && toggle ? (
    <QueryForProcesses
      processIds={visibleProcessIds}
      onCompleted={data => {
        startTransition(() => {
          const newRows = data.dataDump.reduce(
            (acc, dd) => ({ ...acc, [dd.machemetryId]: dd }),
            {} as {
              [processId: string]: DataDumpRow;
            }
          );
          setLastDataDumpRowPerProcess(newRows);
        });
      }}
    />
  ) : null;
}
/** (only used on single-process pages which don't already use HubletViewDataDumpGetter) -- fetch the lastDataDumpRow for the process on this page  */
export default function LastDataDumpRowGetter() {
  const visibleProcessIds = useVisibleProcessIds();
  const isSingleProcessPage = useIsSingleProcessPage();
  const [, setLastDataDumpRow] = useLastDataDumpRow();

  const [toggle, setToggle] = useState(true);
  const ready = isSingleProcessPage && visibleProcessIds.length > 0;
  const processId = useProcessIdOnSingleProcessPage();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();

  // refetch on interval
  useInterval(() => {
    setToggle(false);
    setTimeout(() => setToggle(true));
  }, FETCH_SINGLE_LAST_DATA_DUMP_ROWS_INTERVAL * multiplyNetworkRequestIntervals);

  return ready && toggle && processId ? (
    <QueryForProcess
      processId={processId}
      onCompleted={data => {
        startTransition(() => {
          const lastDataDumpRow =
            data.dataDump.length === 0
              ? null
              : data.dataDump[data.dataDump.length - 1];
          const cleaned =
            lastDataDumpRow && cleanRawDataDumpRow(lastDataDumpRow);
          setLastDataDumpRow(cleaned);
        });
      }}
    />
  ) : null;
}

function QueryForProcess({
  processId,
  onCompleted,
}: {
  processId: string;
  onCompleted: (data: any) => void;
}) {
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);
  useQueryWithErrorHandling("LAST_DATA_DUMP_ROW_QUERY", DATA_DUMP_ROWS_QUERY, {
    client,
    variables: {
      ge: String(now.current - ONE_MINUTE_OF_MS),
      lt: String(now.current),
      machemetryIds: [processId],
      useCache: true,
    },
    onCompleted,
  });
  return null;
}
function QueryForProcesses({
  processIds,
  onCompleted,
}: {
  processIds: string[];
  onCompleted: (data: any) => void;
}) {
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);
  useQueryWithErrorHandling("LAST_DATA_DUMP_ROW_QUERY", DATA_DUMP_ROWS_QUERY, {
    client,
    variables: {
      ge: String(now.current - ONE_MINUTE_OF_MS),
      lt: String(now.current),
      machemetryIds: processIds,
      useCache: true,
    },
    onCompleted,
  });
  return null;
}

export function useLastDataDumpRow(): [
  DataDumpRow | null,
  (lastDataDumpRow: DataDumpRow | null) => void,
] {
  const dispatch = useDispatch();

  return [
    useSelector(state => state.legacy.lastDataDumpRow),
    (lastDataDumpRow: DataDumpRow | null) =>
      dispatch({ type: UPDATE_LAST_DATA_DUMP_ROW, payload: lastDataDumpRow }),
  ];
}

export function useLastDataDumpRowPerProcess(): [
  { [processId: string]: DataDumpRow } | null,
  (
    lastDataDumpRowPerProcess: { [processId: string]: DataDumpRow } | null
  ) => void,
] {
  const dispatch = useDispatch();

  return [
    useSelector(state => state.legacy.lastDataDumpRowPerProcess),
    (lastDataDumpRowPerProcess: { [processId: string]: DataDumpRow } | null) =>
      dispatch({
        type: UPDATE_LAST_DATA_DUMP_ROW_PER_PROCESS,
        payload: lastDataDumpRowPerProcess,
      }),
  ];
}
